import { useEffect, useState } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { useWatch } from "react-hook-form";

import { useValidateZipCode } from "@/api/generated";

type UseZipcodeValidationProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  stateFieldName?: Path<TFieldValues>;
  zipcodeFieldName?: Path<TFieldValues>;
};

export function useZipcodeValidation<TFieldValues extends FieldValues>({
  control,
  stateFieldName,
  zipcodeFieldName,
}: UseZipcodeValidationProps<TFieldValues>) {
  const [zipcodeError, setZipcodeError] = useState<string | undefined>();

  const { mutate: validateZipcode, isPending: isValidatingZipcode } =
    useValidateZipCode();

  const watchedState = useWatch({
    control,
    name: stateFieldName ?? ("state" as Path<TFieldValues>),
  });

  const watchedZipCode = useWatch({
    control,
    name: zipcodeFieldName ?? ("zipCode" as Path<TFieldValues>),
  });

  useEffect(() => {
    const zip_code = (watchedZipCode ?? "").toString();
    const state = (watchedState ?? "").toString();

    // Only validate when both are present and zip is complete+numeric
    if (!state || !/^\d{5}$/.test(zip_code)) {
      setZipcodeError(undefined);
      return;
    }

    validateZipcode(
      { data: { zip_code, state } },
      {
        onSuccess: (data) => {
          setZipcodeError(
            data.is_valid
              ? undefined
              : "ZIP code does not belong to the selected state",
          );
        },
        onError: () => {
          setZipcodeError("Unable to validate ZIP code");
        },
      },
    );
  }, [watchedZipCode, watchedState, validateZipcode]);

  return { zipcodeError, isValidatingZipcode };
}
